import React, { useState } from 'react';
import {
    MoreVertical,
    Edit2,
    Trash2,
    Archive,
    Copy,
    TrendingUp,
    Users,
    DollarSign,
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Zap
} from 'lucide-react';
import { AWARENESS_STAGES, FUNNEL_STAGES } from '../../services/pillarService';

/**
 * Icon mapping for pillar icons
 */
const ICON_MAP = {
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp
};

/**
 * PillarCard Component
 * Displays a marketing pillar with its metrics and funnel stage mappings
 *
 * @param {Object} props
 * @param {Object} props.pillar - The pillar data
 * @param {function} props.onEdit - Callback to edit the pillar
 * @param {function} props.onDelete - Callback to delete the pillar
 * @param {function} props.onArchive - Callback to archive the pillar
 * @param {function} props.onDuplicate - Callback to duplicate the pillar
 * @param {function} props.onClick - Callback when card is clicked
 * @param {boolean} props.compact - Whether to show compact version
 * @param {boolean} props.showMetrics - Whether to show performance metrics
 */
const PillarCard = ({
    pillar,
    onEdit,
    onDelete,
    onArchive,
    onDuplicate,
    onClick,
    compact = false,
    showMetrics = true
}) => {
    const [showMenu, setShowMenu] = useState(false);

    const IconComponent = ICON_MAP[pillar.icon] || Lightbulb;

    // Get funnel stages where this pillar is recommended
    const activeFunnelStages = Object.entries(pillar.funnelMapping || {})
        .filter(([, config]) => config.recommended)
        .map(([stage]) => stage);

    // Calculate a simple performance score (if metrics exist)
    const hasMetrics = pillar.performance?.pipelineMetrics?.avgEngagementRate > 0 ||
        pillar.performance?.revenueMetrics?.avgROAS > 0;

    const handleMenuClick = (e) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleAction = (e, action) => {
        e.stopPropagation();
        setShowMenu(false);
        action && action(pillar);
    };

    // Close menu when clicking outside
    React.useEffect(() => {
        const handleClickOutside = () => setShowMenu(false);
        if (showMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showMenu]);

    return (
        <div
            className="card"
            onClick={() => onClick && onClick(pillar)}
            style={{
                padding: compact ? 'var(--space-3)' : 'var(--space-4)',
                cursor: onClick ? 'pointer' : 'default',
                position: 'relative',
                transition: 'all var(--transition-fast)',
                borderLeft: `4px solid ${pillar.color || 'var(--color-primary)'}`,
                opacity: pillar.isArchived ? 0.6 : 1
            }}
        >
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: compact ? 'var(--space-2)' : 'var(--space-3)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    {/* Icon */}
                    <div style={{
                        width: compact ? '32px' : '40px',
                        height: compact ? '32px' : '40px',
                        borderRadius: 'var(--radius-md)',
                        background: `${pillar.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <IconComponent
                            size={compact ? 16 : 20}
                            style={{ color: pillar.color || 'var(--color-primary)' }}
                        />
                    </div>

                    {/* Title */}
                    <div>
                        <h4 style={{
                            fontSize: compact ? 'var(--font-size-sm)' : 'var(--font-size-base)',
                            fontWeight: 600,
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                        }}>
                            {pillar.name}
                            {pillar.isArchived && (
                                <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                                    Archived
                                </span>
                            )}
                        </h4>
                        {!compact && pillar.description && (
                            <p style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)',
                                margin: 'var(--space-1) 0 0 0',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                            }}>
                                {pillar.description}
                            </p>
                        )}
                    </div>
                </div>

                {/* Menu button */}
                {(onEdit || onDelete || onArchive || onDuplicate) && (
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={handleMenuClick}
                            className="btn btn-ghost btn-sm"
                            style={{ padding: 'var(--space-1)' }}
                        >
                            <MoreVertical size={16} />
                        </button>

                        {showMenu && (
                            <div style={{
                                position: 'absolute',
                                right: 0,
                                top: '100%',
                                marginTop: 'var(--space-1)',
                                background: 'var(--color-bg-primary)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                boxShadow: 'var(--shadow-lg)',
                                zIndex: 100,
                                minWidth: '140px',
                                overflow: 'hidden'
                            }}>
                                {onEdit && (
                                    <button
                                        onClick={(e) => handleAction(e, onEdit)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--color-text-primary)',
                                            textAlign: 'left'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Edit2 size={14} />
                                        Edit
                                    </button>
                                )}
                                {onDuplicate && (
                                    <button
                                        onClick={(e) => handleAction(e, onDuplicate)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--color-text-primary)',
                                            textAlign: 'left'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Copy size={14} />
                                        Duplicate
                                    </button>
                                )}
                                {onArchive && (
                                    <button
                                        onClick={(e) => handleAction(e, onArchive)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--color-text-primary)',
                                            textAlign: 'left'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Archive size={14} />
                                        {pillar.isArchived ? 'Restore' : 'Archive'}
                                    </button>
                                )}
                                {onDelete && (
                                    <button
                                        onClick={(e) => handleAction(e, onDelete)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--color-error)',
                                            textAlign: 'left'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-error-bg, rgba(239, 68, 68, 0.1))'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Trash2 size={14} />
                                        Delete
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Funnel Stage Badges */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-1)',
                marginBottom: compact ? 'var(--space-2)' : 'var(--space-3)'
            }}>
                {activeFunnelStages.length > 0 ? (
                    activeFunnelStages.map(stage => (
                        <span
                            key={stage}
                            className="badge"
                            style={{
                                fontSize: '10px',
                                background: stage === 'TOFU' ? 'var(--info-100)' :
                                    stage === 'MOFU' ? 'var(--warning-100)' :
                                        'var(--success-100)',
                                color: stage === 'TOFU' ? 'var(--info-700)' :
                                    stage === 'MOFU' ? 'var(--warning-700)' :
                                        'var(--success-700)'
                            }}
                            title={FUNNEL_STAGES[stage]?.description}
                        >
                            {stage}
                        </span>
                    ))
                ) : (
                    <span style={{
                        fontSize: '10px',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        No funnel stages assigned
                    </span>
                )}
            </div>

            {/* Weight indicator */}
            {pillar.frequency?.currentWeight > 0 && (
                <div style={{ marginBottom: compact ? 'var(--space-2)' : 'var(--space-3)' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 'var(--font-size-xs)',
                        marginBottom: 'var(--space-1)'
                    }}>
                        <span style={{ color: 'var(--color-text-secondary)' }}>Weight</span>
                        <span style={{ fontWeight: 500 }}>{pillar.frequency.currentWeight}%</span>
                    </div>
                    <div style={{
                        height: '4px',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-full)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${pillar.frequency.currentWeight}%`,
                            height: '100%',
                            background: pillar.color || 'var(--color-primary)',
                            borderRadius: 'var(--radius-full)',
                            transition: 'width var(--transition-normal)'
                        }} />
                    </div>
                </div>
            )}

            {/* Performance Metrics */}
            {showMetrics && !compact && hasMetrics && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    {/* Engagement Rate */}
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--space-1)',
                            marginBottom: '2px'
                        }}>
                            <Users size={12} style={{ color: 'var(--color-text-tertiary)' }} />
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 600
                            }}>
                                {pillar.performance?.pipelineMetrics?.avgEngagementRate?.toFixed(1) || '0'}%
                            </span>
                        </div>
                        <span style={{
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            Engagement
                        </span>
                    </div>

                    {/* ROAS */}
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--space-1)',
                            marginBottom: '2px'
                        }}>
                            <DollarSign size={12} style={{ color: 'var(--color-text-tertiary)' }} />
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 600
                            }}>
                                {pillar.performance?.revenueMetrics?.avgROAS?.toFixed(1) || '0'}x
                            </span>
                        </div>
                        <span style={{
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            ROAS
                        </span>
                    </div>

                    {/* Creatives */}
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--space-1)',
                            marginBottom: '2px'
                        }}>
                            <TrendingUp size={12} style={{ color: 'var(--color-text-tertiary)' }} />
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 600
                            }}>
                                {pillar.performance?.totalCreatives || 0}
                            </span>
                        </div>
                        <span style={{
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            Creatives
                        </span>
                    </div>
                </div>
            )}

            {/* Content guidelines preview */}
            {!compact && pillar.contentGuidelines?.exampleTopics?.length > 0 && (
                <div style={{ marginTop: 'var(--space-3)' }}>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)',
                        display: 'block',
                        marginBottom: 'var(--space-1)'
                    }}>
                        Example topics:
                    </span>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-1)'
                    }}>
                        {pillar.contentGuidelines.exampleTopics.slice(0, 3).map((topic, idx) => (
                            <span
                                key={idx}
                                style={{
                                    fontSize: '10px',
                                    padding: '2px var(--space-2)',
                                    background: 'var(--color-bg-tertiary)',
                                    borderRadius: 'var(--radius-full)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >
                                {topic}
                            </span>
                        ))}
                        {pillar.contentGuidelines.exampleTopics.length > 3 && (
                            <span style={{
                                fontSize: '10px',
                                color: 'var(--color-text-tertiary)',
                                padding: '2px'
                            }}>
                                +{pillar.contentGuidelines.exampleTopics.length - 3} more
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PillarCard;
